/**
 * Data layer for `multi-client-agency-support`. Thin typed fetch wrappers over /api/v1/multi-client-agency-support.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface MultiClientAgencySupportRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListMultiClientAgencySupportResponse {
	count: number;
	items: MultiClientAgencySupportRow[];
}

export interface CreateMultiClientAgencySupportBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/multi-client-agency-support";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListMultiClientAgencySupportResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListMultiClientAgencySupportResponse>(res);
}

export async function create(body: CreateMultiClientAgencySupportBody): Promise<MultiClientAgencySupportRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<MultiClientAgencySupportRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const multiClientAgencySupportQueryKeys = {
	all: ["multi-client-agency-support"] as const,
	list: () => [...multiClientAgencySupportQueryKeys.all] as const,
	detail: (id: string) => [...multiClientAgencySupportQueryKeys.all, id] as const,
};
